<template>
  <div>
    <Section>
      <SectionHeader
        :title="$t('apps.inventory.message.storeage')"
        :subtitle="`${$t('series.lot')} ${lotCode}`"
      />
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="$t('apps.inventory.label.warehouse')" />
        <BaseDropdown
          v-model="input.whId"
          :options="optionWarehouses"
          :disabled="disabled || isEditPage"
          @change="handleChangeWarehouseId"
        />
        </Col>
        <Col className="col-lg-6">
        <Label :text="$t('apps.inventory.label.bin')" />
        <BaseDropdown
          v-model="input.binId"
          :options="optionBinLocations"
          :disabled="disabled || isEditPage"
        />
        </Col>
      </FormRow>
    </Section>

    <Section>
      <SectionHeader :title="$t('message.orderlist')" />
      <FormRow>
        <Col className="col-lg-12">
        <OrderLot
          v-if="orderLot.isRenderOrder"
          :validation="orderLot.trickValidationOrder"
          :data="orderLot.orders"
          :createAble="false"
          :showProductImage="true"
          :selectedWhId="input.whId"
          @onChange="handleChangeOrder"
        />
        </Col>
      </FormRow>
    </Section>

    <Section>
      <SectionHeader :title="$t('apps.inventory.message.date')"/>
      <FormRow>
        <Col className="col-lg-2">
        <Label :text="`${$t('apps.inventory.label.datein')} *`" />
        <BaseInputDatePicker
          v-model="$v.input.dateIn.$model"
          :classNames="[{ 'is-invalid': $v.input.dateIn.$error }]"
          :disabled="disabled"
        />
        </Col>
        <Col className="col-lg-2">
        <Label :text="$t('apps.inventory.label.dateproduct')" />
        <BaseInputDatePicker
          v-model="input.dateProduct"
          :disabled="disabled"
        />
        </Col>
        <Col className="col-lg-2">
        <Label :text="$t('apps.inventory.label.dateexpire')" />
        <BaseInputDatePicker
          v-model="input.dateExpire"
          :disabled="disabled"
        />
        </Col>
      </FormRow>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="$t('apps.inventory.label.note')" />
        <BaseTextArea
          v-model="input.note"
          :disabled="disabled"
          :rows="3"
        />
        </Col>
      </FormRow>
    </Section>

    <BaseFormAction
      v-if="showActionButtons"
      :textCancel="$t('button.cancel')"
      :textSubmit="$t('button.save')"
      :isSubmitted="isSubmitted"
      @onCancel="handleCancel"
      @onSubmit="handleSubmit"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import { cloneDeep } from 'lodash'
import { toDate } from '@/helpers'
import OrderLot from '../custom/OrderLot.vue'

export default {
  components: {
    OrderLot,
  },

  props: {
    state: Object,
    lotCode: String,
    isSubmitted: Boolean,
    disabled: Boolean,
    warehouses: Array,
    products: Array,
    showActionButtons: {
      type: Boolean,
      default: true,
    },
  },

  data () {
    return {
      input: {
        skuId: null,
        unit: null,
        cost: null,
        dateIn: new Date(),
        dateProduct: null,
        dateExpire: null,
        whId: null,
        binId: null,
        note: null,
      },
      inputOriginal: null,
      orderLot: {
        isRenderOrder: false,
        orders: [],
        trickValidationOrder: 0,
      },
    }
  },

  validations: {
    input: {
      skuId: {
        required,
      },
      unit: {
        required,
      },
      dateIn: {
        required,
      },
    },
  },

  computed: {
    ...mapGetters({
      getProductFullNameBySkuId: 'getProductFullNameBySkuId',
    }),
    assignSkuIds () {
      const wh = this.warehouses.find((wh) => wh.warehouse_id === this.input.whId)
      const skuIds = wh.assign_skus.map((sku) => sku.sku_id)
      return skuIds
    },
    defaultOption () {
      return {
        text: this.$t('placeholder.select'),
        value: null,
      }
    },
    optionSkus () {
      const items = this.products.reduce((acc, cur) => {
        const skus = cur.prices.reduce((_acc, _cur) => {
          const useLot = _cur.config && _cur.config.inventory && _cur.config.inventory.use_lot
          const isAssigned = this.assignSkuIds.includes(_cur.sku_id)
          if (useLot && isAssigned) {
            _acc.push(_cur)
          }
          return _acc
        }, [])
        if (skus.length) {
          acc.push(...skus)
        }
        return acc
      }, []).map((sku) => {
        return {
          text: this.getProductFullNameBySkuId(sku.sku_id),
          value: sku.sku_id,
        }
      })
      return [{ ...this.defaultOption }, ...items]
    },
    optionWarehouses () {
      const items = this.warehouses.map((item) => {
        return {
          text: item.warehouse_name,
          value: item.warehouse_id,
        }
      })
      return items
    },
    optionBinLocations () {
      const items = this.warehouses.reduce((acc, cur) => {
        if (cur.warehouse_id === this.input.whId) {
          acc.push(...cur.bin_locations)
        }
        return acc
      }, []).map((bin) => {
        return {
          text: bin.code,
          value: bin._id,
        }
      })
      return [{ ...this.defaultOption }, ...items]
    },
    isEditPage () {
      return !!this.state
    },
  },

  watch: {
    'input.whId': {
      handler () {
        this.$emit('update:selectedWhId', this.input.whId)

        if (!this.isEditPage) {
          this.orderLot.isRenderOrder = false
          this.orderLot.orders = []
          setTimeout(() => this.orderLot.isRenderOrder = true, 10)
        }
      },
      deep: true,
    },
    input: {
      handler: function () {
        this.$emit('update:form', this.input)
      },
      deep: true,
    },
  },

  created () {
    if (this.state) {
      this.setInput(this.state)
    } else {
      this.input.whId = this.warehouses.find((item) => item.primary).warehouse_id
      this.inputOriginal = cloneDeep(this.input)
    }
    this.orderLot.isRenderOrder = true
  },

  methods: {
    handleCancel () {
      if (this.$listeners.onCancel) {
        this.$emit('onCancel')
      } else {
        this.input = cloneDeep(this.inputOriginal)
        this.scrollToTop(600)
      }
    },
    handleSubmit () {
      this.$v.$touch()
      this.orderLot.trickValidationOrder++
      if (this.$v.$invalid) {
        this.scrollToTop(600)
      } else {
        this.$emit('onSubmit', this.input)
      }
    },
    setInput (state) {
      this.input.skuId = state.skuId
      this.input.unit = state.unitBalance
      this.input.cost = state.cost
      this.input.dateIn = toDate(state.dateIn)
      this.input.dateProduct = toDate(state.dateProduct)
      this.input.dateExpire = toDate(state.dateExpire)
      this.input.whId = state.whId
      this.input.binId = state.binLocationId
      this.input.note = state.note
      this.inputOriginal = cloneDeep(this.input)

      this.orderLot.orders.push({
        sku_id: state.skuId,
        item: this.getProductFullNameBySkuId(state.skuId),
        quantity: state.unitBalance,
        cost: state.cost,
        selectize: {
          loading: false,
          updateOption: null,
          trickValidation: 0,
        },
        disabledSku: true,
        disabledQty: false,
        disabledCost: false,
      })
    },
    handleChangeWarehouseId () {
      this.input.binId = null
      this.input.skuId = null
    },
    handleChangeOrder(order) {
      this.input.skuId = order.skuId
      this.input.unit = order.quantity
      this.input.cost = order.cost
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/combine/ImsLotNumberForm.vue